import { PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty, PropertiesPanelPropertyContent } from './propertiesPanelProperty';
import React, { useRef } from 'react';
import { _FileInputContainer, FileInput } from '../input';
import { Icon } from '../icon';
import TrashIcon from '../icons/trash-alt.svg';
import styled from 'styled-components';
import { T } from '../helpers/helpers';
import { useDropdown } from '../hooks/useDropdown';
import { Spinner } from '../spinner';
import { BasicButton } from '../buttons';

const CompactSpinner = styled(Spinner)`
    width: 24px;
    height: 24px;
`;

const CompactFileInputWrapper = styled.div`
    ${_FileInputContainer} ${BasicButton} {
        padding: 4px 8px;
        font-size: 12px;
        font-weight: ${(props) => props.theme.app.component.textButton.fontWeight};
        min-height: unset;
        min-width: unset;
        background: none;
        border: none;
        color: ${(props) => props.theme.app.component.textButton.primary.default.color};
        cursor: pointer;
        white-space: nowrap;

        &:hover:not([disabled]) {
            background: none;
            color: ${(props) => props.theme.app.component.textButton.primary.hover.color};
        }

        &[disabled] {
            opacity: 0.5;
            cursor: default;
            background: none;
        }
    }
`;

const ImagesPreviewsContainer = styled(PropertiesPanelPropertyContent)`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 8px;
`;

const PreviewImage = styled.img`
    width: 32px;
    height: 32px;
    object-fit: cover;
    cursor: pointer;
`;

const DropdownImage = styled.img`
    width: 100px;
`;
type PreviewProps = {
    fileName: string;
    src: string;
    alt: string;
};
const Preview: React.FC<PreviewProps> = ({ fileName, src, alt }) => {
    const [setOpen, _, isOpen, Dropdown] = useDropdown();
    return (
        <div>
            <PreviewImage src={src} alt={alt} onClick={(e) => setOpen(e.currentTarget)} />
            {isOpen && (
                <Dropdown>
                    {fileName}
                    <br />
                    <br />
                    <DropdownImage src={src} alt={alt} />
                </Dropdown>
            )}
        </div>
    );
};

type Image = {
    fileName: string;
    src: string;
    customPreview?: React.ReactNode;
};
export type PropertiesPanelImagePropertyProps = {
    id: string;
    label: React.ReactNode;
    images: Image[] | null;
    onUpload?: (files: FileList | null) => void;
    onDelete?: () => void;
    disabled?: boolean;
    loading?: boolean;
    accept?: string;
};
export const PropertiesPanelImageProperty: React.FC<PropertiesPanelImagePropertyProps> = ({
    id,
    label,
    images,
    onUpload,
    onDelete,
    disabled,
    loading,
    accept = 'image/*'
}) => {
    var fileInputRef = useRef<{ reset: () => void }>(null);

    return (
        <PropertiesPanelProperty>
            <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
            <PropertiesPanelControl>
                {loading && <CompactSpinner />}
                {!loading && (
                    <CompactFileInputWrapper>
                        <FileInput
                            ref={fileInputRef}
                            multiple
                            label={T._('Upload', 'Admin')}
                            onChange={(files) => {
                                onUpload?.(files);
                                fileInputRef!.current!.reset();
                            }}
                            disabled={disabled}
                            accept={accept}
                        />
                    </CompactFileInputWrapper>
                )}
                {!loading && !!images?.length && (
                    <Icon $hoverable onClick={onDelete} disabled={disabled}>
                        <TrashIcon />
                    </Icon>
                )}
                {!loading &&
                    !!images &&
                    images.length === 1 &&
                    (images[0].customPreview ?? (
                        <Preview key={images[0].fileName} fileName={images[0].fileName} src={images[0].src} alt={images[0].fileName} />
                    ))}
            </PropertiesPanelControl>
            {!loading && !!images && images.length > 1 && (
                <ImagesPreviewsContainer>
                    {images.map(
                        (image) => image.customPreview ?? <Preview key={image.fileName} fileName={image.fileName} src={image.src} alt={image.fileName} />
                    )}
                </ImagesPreviewsContainer>
            )}
        </PropertiesPanelProperty>
    );
};
